/**
 * 商品模拟数据 - 李宁主题
 */

import { randomBetween, randomChoice } from '@/mock/mockData';

export interface MockProductItem {
  id: string;
  name: string;
  category: string;
  brand: string;
  price: number;
  stock: number;
  sales: number;
  image: string;
  status: 'active' | 'inactive' | 'draft';
  createTime: string;
  updateTime: string;
  description: string;
  sku: string;
  weight: number;
  tags: string[];
}

// 李宁产品分类
const categories = [
  '李宁篮球鞋', '李宁跑步鞋', '李宁羽毛球鞋', '李宁训练鞋',
  '李宁T恤', '李宁短裤', '李宁外套', '李宁运动长裤',
  '李宁运动包', '李宁帽子', '李宁袜子',
  '李宁羽毛球拍', '李宁羽毛球', '李宁羽毛球线',
  '李宁休闲鞋', '李宁休闲服装'
];

// 以李宁为主的中国运动品牌
const brands = ['李宁', '安踏', '匹克', '特步', '鸿星尔克', '乔丹体育', '德尔惠', '回力'];

const statuses: ('active' | 'inactive' | 'draft')[] = ['active', 'inactive', 'draft'];
const tags = ['新品上市', '热销爆款', '限时特价', '明星同款', '专业推荐', '经典款', '透气轻便', '高性能'];

// 李宁产品名称模板
const productNames = [
  // 篮球鞋系列
  '李宁韦德之道10代', '李宁韦德之道9代', '李宁驭帅15', '李宁空袭7',
  '李宁全城9', '李宁音速9', '李宁闪击7', '李宁风暴',
  
  // 跑步鞋系列
  '李宁超轻19代', '李宁云四代', '李宁赤兔6代', '李宁飞电3',
  '李宁绝影', '李宁风云', '李宁疾风', '李宁云马',
  
  // 羽毛球鞋系列
  '李宁突袭TD', '李宁雷霆', '李宁风暴战靴', '李宁风动',
  '李宁音爆', '李宁燃烧', '李宁战戟', '李宁游侠',
  
  // 服装系列
  '李宁运动T恤', '李宁篮球短裤', '李宁羽毛球外套', '李宁训练背心',
  '李宁运动夹克', '李宁休闲卫衣', '李宁跑步短裤', '李宁健身服',
  
  // 配件系列
  '李宁双肩背包', '李宁运动帽', '李宁专业袜', '李宁护腕',
  '李宁运动毛巾', '李宁水杯', '李宁手套', '李宁护膝',
  
  // 羽毛球装备
  '李宁风动9000i', '李宁风暴72', '李宁突袭A900', '李宁音爆A90',
  '李宁A+600羽毛球', '李宁A+300羽毛球', '李宁7号线', '李宁95线'
];

/**
 * 生成单个商品数据
 */
const generateProduct = (index: number): MockProductItem => {
  const category = randomChoice(categories);
  const brand = randomChoice(brands);
  const status = randomChoice(statuses);
  const productTags = tags.slice(0, randomBetween(1, 4));
  
  // 根据索引选择产品名称，如果超出范围则使用模板生成
  let productName: string;
  if (index < productNames.length) {
    productName = productNames[index];
  } else {
    productName = `${brand} ${category} ${Math.floor(index / productNames.length) + 1}号`;
  }
  
  // 根据分类设置价格范围
  let priceRange = { min: 199, max: 899 };
  if (category.includes('篮球鞋') || category.includes('羽毛球拍')) {
    priceRange = { min: 399, max: 1599 };
  } else if (category.includes('跑步鞋') || category.includes('训练鞋')) {
    priceRange = { min: 299, max: 1299 };
  } else if (category.includes('外套') || category.includes('夹克')) {
    priceRange = { min: 259, max: 799 };
  } else if (category.includes('T恤') || category.includes('短裤')) {
    priceRange = { min: 99, max: 399 };
  }
  
  return {
    id: `lining-${String(index + 1).padStart(6, '0')}`,
    name: productName,
    category,
    brand,
    price: randomBetween(priceRange.min, priceRange.max),
    stock: randomBetween(0, 500),
    sales: randomBetween(0, 3000),
    image: `https://picsum.photos/400/400?random=${Math.floor(Math.random() * 1000)}`,
    status,
    sku: `LN${String(index + 1).padStart(8, '0')}`,
    weight: randomBetween(200, 1500) / 100,
    description: generateProductDescription(productName, category, brand),
    tags: productTags,
    createTime: new Date(Date.now() - randomBetween(0, 365) * 24 * 60 * 60 * 1000).toISOString(),
    updateTime: new Date(Date.now() - randomBetween(0, 30) * 24 * 60 * 60 * 1000).toISOString(),
  };
};

/**
 * 生成产品描述
 */
const generateProductDescription = (name: string, category: string, brand: string): string => {
  const descriptions = {
    篮球鞋: `${name}采用${brand}最新科技，提供卓越的缓震性能和支撑保护，助力您在球场上展现非凡实力。`,
    跑步鞋: `${name}轻量化设计，配备专业缓震中底，为跑者提供舒适的跑步体验和持久的动力支持。`,
    羽毛球鞋: `${name}专为羽毛球运动设计，具备出色的抓地力和灵活性，让您在球场上游刃有余。`,
    训练鞋: `${name}多功能训练鞋，适合各种健身运动，提供全方位的支撑和舒适感。`,
    T恤: `${name}采用高品质面料制作，透气排汗，穿着舒适，是运动和休闲的理想选择。`,
    短裤: `${name}轻便舒适，快干透气，为您的运动提供最大的自由度和舒适感。`,
    外套: `${name}时尚设计，防风保暖，无论运动还是日常穿搭都能展现您的活力风采。`,
    运动包: `${name}大容量设计，多分层收纳，是您运动出行的最佳伴侣。`,
    羽毛球拍: `${name}采用高科技材料制造，手感出色，控制精准，助您在赛场上发挥最佳水平。`,
    default: `${name}是${brand}精心打造的高品质产品，融合了先进技术和人性化设计，为您带来卓越的使用体验。`
  };
  
  for (const [key, desc] of Object.entries(descriptions)) {
    if (category.includes(key)) {
      return desc;
    }
  }
  
  return descriptions.default;
};

/**
 * 生成商品列表数据
 */
export const generateProductList = (page: number, pageSize: number, filters: any = {}): Promise<{
  data: MockProductItem[];
  total: number;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    // 模拟网络延迟
    setTimeout(() => {
      const totalCount = 1000;
      const allProducts: MockProductItem[] = [];
      
      // 生成所有商品数据
      for (let i = 0; i < totalCount; i++) {
        allProducts.push(generateProduct(i));
      }
      
      // 应用筛选条件
      let filteredProducts = allProducts;
      
      if (filters.name) {
        filteredProducts = filteredProducts.filter(product => 
          product.name.toLowerCase().includes(filters.name.toLowerCase())
        );
      }
      
      if (filters.category) {
        filteredProducts = filteredProducts.filter(product => 
          product.category === filters.category
        );
      }
      
      if (filters.brand) {
        filteredProducts = filteredProducts.filter(product => 
          product.brand === filters.brand
        );
      }
      
      if (filters.status) {
        filteredProducts = filteredProducts.filter(product => 
          product.status === filters.status
        );
      }
      
      // 分页处理
      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const data = filteredProducts.slice(start, end);
      
      resolve({
        data,
        total: filteredProducts.length,
        success: true,
      });
    }, randomBetween(300, 800)); // 模拟网络延迟
  });
};

/**
 * 获取单个商品详情
 */
export const getProductDetail = (id: string): Promise<{
  data: MockProductItem | null;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const productIndex = parseInt(id.replace('lining-', '')) - 1;
      const product = productIndex >= 0 ? generateProduct(productIndex) : null;
      
      resolve({
        data: product,
        success: true,
      });
    }, randomBetween(200, 500));
  });
};

/**
 * 获取商品分类列表
 */
export const getProductCategories = (): Promise<{
  data: Array<{ label: string; value: string }>;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: categories.map(category => ({ label: category, value: category })),
        success: true,
      });
    }, 100);
  });
};

/**
 * 获取商品品牌列表
 */
export const getProductBrands = (): Promise<{
  data: Array<{ label: string; value: string }>;
  success: boolean;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        data: brands.map(brand => ({ label: brand, value: brand })),
        success: true,
      });
    }, 100);
  });
};